import { inject, Injectable } from '@angular/core';
import { OrderItemStatusEnumResponseDto } from '@core/services/apiclient';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { isSubscriptionGroup, mapToTreeListRow, OrderItemRow, TreeListRowData } from '../models/orders.model';
import { OrdersService } from './orders.service';

@Injectable({
  providedIn: 'root',
})
export class OrdersSubscriptionGroupService {
  private readonly ordersService = inject(OrdersService);

  normalizeSelection(selection: TreeListRowData[]): TreeListRowData[] {
    const rows = selection ?? [];
    if (!rows.length) return [];

    const parentIds = new Set<number>(rows.filter((r) => r?.hasItems === true).map((r) => r.id));
    if (!parentIds.size) return rows;

    return rows.filter((r) => r?.parentId == null || !parentIds.has(r.parentId));
  }

  /**
   * Splits a tree-list selection into subscription groups (acted on as a whole formula) and
   * individual order items (acted on folder by folder).
   *
   * `childrenAreComplete` states whether `allRows` holds every child of a group. It is `false` when
   * the list applies an event session filter, which loads a single child per group: promoting that
   * child to its group would widen the action to the whole formula, since the printing endpoints
   * expand a subscription id to all of its sessions with no event predicate.
   */
  resolveSubscriptionSelection(
    selection: TreeListRowData[],
    allRows: TreeListRowData[],
    childrenAreComplete: boolean,
  ): {
    selectedSubscriptionGroups: TreeListRowData[];
    selectedOrderItems: TreeListRowData[];
  } {
    const selectedRows = selection;
    const selectedIds = new Set(selectedRows.map((r) => r.id));

    const directlySelectedGroups = selectedRows.filter((row) => row.hasItems);

    const fullySelectedByChildren = childrenAreComplete
      ? allRows
          .filter((row) => row.hasItems && !selectedIds.has(row.id))
          .filter((group) => {
            const children = allRows.filter((row) => row.parentId === group.id);
            return children.length > 0 && children.every((child) => selectedIds.has(child.id));
          })
      : [];

    const selectedSubscriptionGroups = [...directlySelectedGroups, ...fullySelectedByChildren];
    const fullySelectedGroupIds = new Set(selectedSubscriptionGroups.map((g) => g.id));

    const selectedOrderItems = selectedRows.filter(
      (row) => !row.hasItems && (row.parentId == null || !fullySelectedGroupIds.has(row.parentId)),
    );

    return {
      selectedSubscriptionGroups,
      selectedOrderItems,
    };
  }

  isSubscriptionGroupEntireForPayment(group: TreeListRowData): boolean {
    const reservedCount = group.reservedOrderRowsCount ?? 0;
    const totalCount = group.orderItemCount ?? 0;
    const canceledCount = group.canceledOrderRowsCount ?? 0;

    if (totalCount === 0) {
      return false;
    }

    return (
      reservedCount === totalCount || (reservedCount > 0 && reservedCount + canceledCount === totalCount)
    );
  }

  isSubscriptionGroupEntireForRefund(group: TreeListRowData): boolean {
    const paidCount = group.paidOrderRowsCount ?? 0;
    const editedCount = group.editedOrderRowsCount ?? 0;
    const canceledCount = group.canceledOrderRowsCount ?? 0;
    const totalCount = group.orderItemCount ?? 0;
    const refundableCount = paidCount + editedCount;

    if (totalCount === 0 || refundableCount === 0) {
      return false;
    }

    return (
      refundableCount === totalCount || (canceledCount > 0 && canceledCount + refundableCount === totalCount)
    );
  }

  calculateReservedAmountForSubscriptionGroup(group: TreeListRowData): Observable<TreeListRowData> {
    return this.ordersService.getSubscriptionOrderItems(group.id).pipe(
      map((items) => {
        const reservedItems = (items ?? []).filter(
          (item) => item.statusGroup === OrderItemStatusEnumResponseDto.Reserved,
        );
        const reservedAmount = reservedItems.reduce((acc, item) => acc + (item.amount ?? 0), 0);
        return {
          ...group,
          amount: reservedAmount,
        };
      }),
    );
  }

  calculateRefundAmountForSubscriptionGroup(group: TreeListRowData): Observable<TreeListRowData> {
    return this.ordersService.getSubscriptionOrderItems(group.id).pipe(
      map((items) => {
        const refundableItems = (items ?? []).filter(
          (item) =>
            item.statusGroup === OrderItemStatusEnumResponseDto.Paid ||
            item.statusGroup === OrderItemStatusEnumResponseDto.Edited,
        );
        const refundAmount = refundableItems.reduce((acc, item) => acc + (item.amount ?? 0), 0);
        return {
          ...group,
          amount: refundAmount,
        };
      }),
    );
  }

  isPartialPaymentGroup(row: TreeListRowData): boolean {
    return isSubscriptionGroup(row) && !this.isSubscriptionGroupEntireForPayment(row);
  }

  isEntirePaymentGroup(row: TreeListRowData): boolean {
    return isSubscriptionGroup(row) && this.isSubscriptionGroupEntireForPayment(row);
  }

  isPartialRefundGroup(row: TreeListRowData): boolean {
    return isSubscriptionGroup(row) && !this.isSubscriptionGroupEntireForRefund(row);
  }

  isEntireRefundGroup(row: TreeListRowData): boolean {
    return isSubscriptionGroup(row) && this.isSubscriptionGroupEntireForRefund(row);
  }

  isRegularReserved(row: TreeListRowData): boolean {
    return !isSubscriptionGroup(row) && row?.statusGroup === OrderItemStatusEnumResponseDto.Reserved;
  }

  isRegularRefundable(row: TreeListRowData): boolean {
    return (
      !isSubscriptionGroup(row) &&
      (row?.statusGroup === OrderItemStatusEnumResponseDto.Paid ||
        row?.statusGroup === OrderItemStatusEnumResponseDto.Edited)
    );
  }

  prepareSelectionWithSubscriptionGroups(
    selection: TreeListRowData[],
    statusFilter: (status: OrderItemStatusEnumResponseDto) => boolean,
    shouldProcessSubscriptionGroup: (group: TreeListRowData) => boolean,
  ): Observable<TreeListRowData[]> {
    const normalized = this.normalizeSelection(selection);
    const regularItems: TreeListRowData[] = [];
    const subscriptionGroups: TreeListRowData[] = [];

    normalized.forEach((row) => {
      if (isSubscriptionGroup(row)) {
        if (shouldProcessSubscriptionGroup(row)) {
          subscriptionGroups.push(row);
        } else if (statusFilter(row?.statusGroup)) {
          regularItems.push(row);
        }
      } else if (statusFilter(row?.statusGroup)) {
        regularItems.push(row);
      }
    });

    if (subscriptionGroups.length === 0) {
      return of(regularItems);
    }

    const subscriptionItems$ = subscriptionGroups.map((group) =>
      this.ordersService.getSubscriptionOrderItems(group.id).pipe(
        map((items) => {
          const parent = group;
          return (items ?? [])
            .map((item) => {
              const mapped = mapToTreeListRow(item as OrderItemRow);
              return {
                ...mapped,
                customer: mapped.customer ?? parent?.customer,
                parentLabel: parent?.label,
              };
            })
            .filter((item) => statusFilter(item.statusGroup));
        }),
      ),
    );

    return forkJoin(subscriptionItems$).pipe(
      map((results) => {
        const allSubscriptionItems = results.flat();
        return [...regularItems, ...allSubscriptionItems];
      }),
    );
  }
}
